import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  ShoppingBag,
  ArrowRight,
  Wrench,
  Eye,
  Sun,
  MapPin,
  Phone,
  Clock,
  Gift,
  ShieldCheck,
  Package,
  Truck,
  Banknote,
  Sparkles,
  Instagram,
} from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import LensConfigurator from "./pages/LensConfigurator";
import QuoteCart from "./pages/QuoteCart";
import ProductMarquee from "./pages/ProductMarquee";
import AdminPanel from "./pages/AdminPanel";
import SpinningGlasses from "./pages/SpinningGlasses";
import useCustomProducts from "./hooks/useCustomProducts";
import { useCategories, useServices } from "./hooks/useCatalog";
import {
  repairServices,
  WHATSAPP_NUMBER,
  formatPrice,
} from "./data/products";

const HERO_IMAGE = null;
const REPAIRS_IMAGE =
  "https://images.pexels.com/photos/5715905/pexels-photo-5715905.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function PromoMarquee() {
  const message = "POR COMPRA DE CUALQUIER LENTE RECIBE UN PRODUCTO DE REGALO";
  const items = Array.from({ length: 8 });
  return (
    <div className="bg-[#1C1C1C] overflow-hidden border-b-2 border-[#B8933A]" data-testid="promo-marquee">
      <div className="marquee-track py-2.5">
        {items.map((_, i) => (
          <span key={i} className="marquee-item text-[#E9C46A] text-xs md:text-sm font-semibold tracking-[0.3em]">
            <Gift className="w-4 h-4" /> {message} <span className="text-[#B8933A]">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Header({ cartCount, onOpenCart }) {
  return (
    <header className="bg-[#FAF9F6]/95 backdrop-blur-md border-b border-[#EAEAEA]" data-testid="site-header">
      <div className="h-[3px] w-full bg-gradient-to-r from-[#B8933A] via-[#E9C46A] to-[#B8933A]" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        <a href="#home" className="flex flex-col leading-none" data-testid="brand-logo">
          <span className="font-serif-display text-3xl md:text-4xl tracking-tight text-[#1C1C1C] font-bold">
            La Clínica del Lente
          </span>
          <span className="flex items-center gap-2 mt-1.5">
            <span className="h-[2px] w-8 bg-gradient-to-r from-[#B8933A] to-[#E9C46A]" />
            <span className="eyebrow text-[9px] text-[#595959]">
              Óptica · Reparaciones
            </span>
            <span className="h-[2px] w-8 bg-gradient-to-r from-[#E9C46A] to-[#B8933A]" />
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="main-nav">
          <a href="#esencial" className="text-sm text-[#1C1C1C] hover:text-[#B8933A] transition-colors" data-testid="nav-esencial">Esencial</a>
          <a href="#clip" className="text-sm text-[#1C1C1C] hover:text-[#B8933A] transition-colors" data-testid="nav-clip">Clips</a>
          <a href="#deportivos" className="text-sm text-[#1C1C1C] hover:text-[#B8933A] transition-colors" data-testid="nav-deportivos">Deportivos</a>
          <a href="#sol" className="text-sm text-[#1C1C1C] hover:text-[#B8933A] transition-colors" data-testid="nav-sol">Sol</a>
          <a href="#reparaciones" className="text-sm text-[#1C1C1C] hover:text-[#B8933A] transition-colors" data-testid="nav-reparaciones">Reparaciones</a>
          <a href="#contacto" className="text-sm text-[#1C1C1C] hover:text-[#B8933A] transition-colors" data-testid="nav-contacto">Contacto</a>
        </nav>

        <button
          onClick={onOpenCart}
          className="hidden md:flex items-center gap-2 border border-[#1C1C1C] px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#1C1C1C] hover:text-white transition-colors"
          data-testid="open-cart-button"
        >
          <ShoppingBag className="w-4 h-4" />
          <span>Cotizar</span>
          <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1 bg-[#1C1C1C] text-white text-[10px]" data-testid="cart-count">
            {cartCount}
          </span>
        </button>
      </div>
    </header>
  );
}

function Hero({ onOpenCart }) {
  return (
    <section id="home" className="relative border-b border-[#EAEAEA]" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 space-y-8 animate-fade-up">
          <span className="eyebrow" data-testid="hero-eyebrow">
            Atención personalizada · Desde 2011
          </span>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#1C1C1C]">
            Tu visión,
            <br />
            cuidada con <em className="italic font-normal">detalle</em>.
          </h1>
          <p className="text-base md:text-lg text-[#595959] leading-relaxed max-w-xl">
            Lentes ópticos, deportivos, infantiles, de sol y reparaciones realizados
            con precisión artesanal. Configura tu cristal y recibe la cotización
            directa en WhatsApp — sin filas, sin formularios largos.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="#esencial" className="btn-primary text-center" data-testid="hero-cta-explore">
              Ver catálogo
            </a>
            <button onClick={onOpenCart} className="btn-outline" data-testid="hero-cta-cart">
              Cotizar ahora
            </button>
          </div>
        </div>

        <div className="lg:col-span-6 relative animate-fade-up delay-2">
          <SpinningGlasses />
        </div>
      </div>
    </section>
  );
}

function InfoStrip() {
  const items = [
    { icon: Truck, title: "Entrega 24h en adelante", detail: "Preparamos tu pedido con prioridad." },
    { icon: Package, title: "Estuche + paño incluidos", detail: "Todos nuestros lentes traen estuche rígido y paño microfibra." },
    { icon: ShieldCheck, title: "Garantía 3 meses", detail: "Por fallas propias de fábrica. No cubre golpes ni daños por uso indebido." },
    { icon: Gift, title: "Regalo en cada compra", detail: "Por la compra de cualquier lente recibes un producto de regalo." },
  ];
  return (
    <section className="bg-[#1C1C1C] text-[#FAF9F6] py-14" data-testid="info-strip">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-4" data-testid={`info-item-${i}`}>
            <it.icon className="w-6 h-6 text-[#E9C46A] shrink-0 mt-1" />
            <div>
              <h4 className="font-serif-display text-xl mb-1 leading-tight">{it.title}</h4>
              <p className="text-sm text-[#b8b8b8] leading-relaxed">{it.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SunglassesSection({ products, onAddDirect }) {
  const resolve = (src) => !src ? "" : src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return (
    <section id="sol" className="py-24 md:py-32 border-b border-[#EAEAEA] bg-[#F0F0EE]/40" data-testid="sol-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-xl space-y-4">
            <span className="eyebrow flex items-center gap-2">
              <Sun className="w-3 h-3" /> Lentes de sol
            </span>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#1C1C1C] leading-tight tracking-tight font-semibold">
              Protección con <em className="italic font-normal">carácter</em>.
            </h2>
            <p className="text-[#595959] text-base leading-relaxed">
              Cristales polarizados con filtro UV400. Todos incluyen estuche rígido y paño.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
          {products.map((p) => (
            <article key={p.id} className="product-card group flex flex-col" data-testid={`sun-card-${p.id}`}>
              <div className="product-image-wrap">
                <img src={resolve(p.image)} alt={p.alt || p.name} loading="lazy" />
              </div>
              <div className="pt-5 flex flex-col gap-2">
                <span className="eyebrow text-[10px] text-[#B8933A]">{p.brand}</span>
                <h3 className="font-serif-display text-2xl text-[#1C1C1C] leading-tight">{p.name}</h3>
                <p className="text-sm text-[#595959]">{p.description}</p>
                <div className="font-serif-display text-xl text-[#1C1C1C] font-semibold">
                  {formatPrice(p.price)}
                </div>
                <button
                  onClick={() => onAddDirect(p, "sol")}
                  className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-[#1C1C1C] mt-2 group-hover:gap-3 transition-all self-start"
                  data-testid={`sun-cta-${p.id}`}
                >
                  Cotiza
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function RepairsSection({ onAddRepair, services = [] }) {
  return (
    <section id="reparaciones" className="py-24 md:py-32 border-b border-[#EAEAEA]" data-testid="reparaciones-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32 space-y-6">
          <span className="eyebrow flex items-center gap-2">
            <Wrench className="w-3 h-3" /> Taller de reparaciones
          </span>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#1C1C1C] leading-tight tracking-tight font-semibold">
            Le damos nueva vida a tus <em className="italic font-normal">monturas</em>.
          </h2>
          <p className="text-[#595959] leading-relaxed">
            Cambios de cristales, soldaduras, plaquetas, bisagras y barillas.
            Diagnóstico honesto y trabajo a la vista.
          </p>

          <div className="border-l-2 border-[#B8933A] pl-4 py-2 bg-[#E9C46A]/10 space-y-2">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-[#B8933A] mt-0.5 shrink-0" />
              <p className="text-sm text-[#1C1C1C] leading-relaxed">
                <strong>Incluye limpieza profunda de cortesía</strong> en todas las reparaciones.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <Banknote className="w-4 h-4 text-[#B8933A] mt-0.5 shrink-0" />
              <p className="text-sm text-[#1C1C1C] leading-relaxed">
                <strong>Pago en efectivo únicamente</strong> para servicios de reparación.
              </p>
            </div>
          </div>

          <div className="product-image-wrap aspect-[16/10] mt-6">
            <img src={REPAIRS_IMAGE} alt="Óptico profesional reparando lentes en taller" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="lg:col-span-7 space-y-4">
          {services.map((r, i) => (
            <div key={r.id} className="border border-[#EAEAEA] p-6 md:p-8 hover:border-[#B8933A] transition-colors group" data-testid={`repair-card-${r.id}`}>
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="eyebrow text-[10px] text-[#B8933A] mb-2">Servicio · 0{i + 1}</div>
                  <h3 className="font-serif-display text-2xl md:text-3xl text-[#1C1C1C] mb-2 leading-tight">{r.name}</h3>
                  <p className="text-sm text-[#595959] leading-relaxed max-w-xl">{r.detail}</p>
                  <div className="font-serif-display text-xl text-[#1C1C1C] font-semibold mt-3">
                    {r.quoteOnly ? (
                      <span className="text-[#595959] text-sm">Se cotiza según medida</span>
                    ) : r.priceFrom ? (
                      <>Desde {formatPrice(r.price)}</>
                    ) : (
                      formatPrice(r.price)
                    )}
                  </div>
                </div>
                <button onClick={() => onAddRepair(r)} className="shrink-0 flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-[#1C1C1C] group-hover:gap-3 transition-all" data-testid={`repair-add-${r.id}`}>
                  Agregar
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer id="contacto" className="bg-[#1C1C1C] text-[#FAF9F6] py-20 md:py-28" data-testid="site-footer">
      <div className="h-[3px] w-full bg-gradient-to-r from-[#B8933A] via-[#E9C46A] to-[#B8933A] -mt-20 mb-20" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5 space-y-4">
          <h3 className="font-serif-display text-3xl md:text-4xl leading-tight font-semibold">
            La Clínica del Lente
          </h3>
          <div className="h-[2px] w-20 bg-gradient-to-r from-[#B8933A] via-[#E9C46A] to-[#B8933A]" />
          <p className="text-sm text-[#b8b8b8] leading-relaxed max-w-md">
            Atención personalizada en lentes ópticos, deportivos, infantiles, de sol y reparaciones.
            Cotiza por WhatsApp y recibe una respuesta humana, rápida y honesta.
          </p>
        </div>

        <div className="md:col-span-4 space-y-5">
          <span className="eyebrow text-[#E9C46A]">Contacto</span>
          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 mt-0.5 text-[#E9C46A]" />
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="hover:text-white transition-colors" data-testid="footer-whatsapp">
                +56 9 8575 4039 · WhatsApp
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-[#E9C46A] shrink-0" />
              <a
                href="https://maps.app.goo.gl/tKeeSbqXRCVQ5N5QA"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#b8b8b8] hover:text-white transition-colors"
                data-testid="footer-address"
              >
                Brasil 591, Local 8 · Galería Ancarola
                <br />
                La Serena, Chile
                <span className="block eyebrow text-[9px] text-[#E9C46A] mt-1">Ver en Google Maps →</span>
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="w-4 h-4 mt-0.5 text-[#E9C46A]" />
              <span className="text-[#b8b8b8]">Lun a Vie · 10:00 — 18:00</span>
            </li>
            <li className="flex items-start gap-3">
              <Instagram className="w-4 h-4 mt-0.5 text-[#E9C46A]" />
              <a
                href="https://www.instagram.com/la_clinica_del_lente?igsh=YTBnMTNpb3k2anFr"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
                data-testid="footer-instagram"
              >
                @la_clinica_del_lente
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3 space-y-4">
          <span className="eyebrow text-[#E9C46A]">Menú</span>
          <ul className="space-y-3 text-sm">
            <li><a href="#esencial" className="hover:text-white transition-colors">Colección Esencial</a></li>
            <li><a href="#clip" className="hover:text-white transition-colors">Con Clip</a></li>
            <li><a href="#deportivos" className="hover:text-white transition-colors">Deportivos / Niños</a></li>
            <li><a href="#sol" className="hover:text-white transition-colors">Lentes de sol</a></li>
            <li><a href="#reparaciones" className="hover:text-white transition-colors">Reparaciones</a></li>
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16 pt-8 border-t border-[#2a2a2a] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <p className="text-xs text-[#b8b8b8] tracking-wide">
          © {new Date().getFullYear()} La Clínica del Lente. Todos los derechos reservados.
        </p>
        <div className="flex items-center gap-6">
          <a href="/admin" className="eyebrow text-[#E9C46A] hover:text-white transition-colors" data-testid="footer-admin-link">
            Acceso administrador
          </a>
          <p className="eyebrow text-[#E9C46A]">Hecho con cuidado en Chile</p>
        </div>
      </div>
    </footer>
  );
}

const Home = () => {
  const [cartItems, setCartItems] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");

  const { custom } = useCustomProducts();
  const { categories } = useCategories();
  const { services } = useServices();

  // Helper: trae categoría por kind con fallback
  const cat = (kind) => categories.find((c) => c.kind === kind) || { eyebrow: "", title: "", description: "", priceTag: "" };

  // Todos los productos vienen del backend ahora (seed inicial + lo que agregue el admin)
  const byKind = (kind) => custom.filter((c) => c.kind === kind && c.active !== false);
  const mergedBasic = byKind("basico");
  const mergedClip1 = byKind("clip1");
  const mergedClip5 = byKind("clip5");
  const mergedSport = byKind("deportivo");
  const mergedKids = byKind("flexit");
  const mergedSun = byKind("sol");

  const openConfigurator = (product) => {
    setActiveProduct(product);
    setConfigOpen(true);
  };

  const addOpticalConfigured = (config) => {
    setCartItems((prev) => [
      ...prev,
      {
        ...config,
        type: "optico",
        price: activeProduct?.price,
        brand: activeProduct?.brand,
        productImage: activeProduct?.image,
      },
    ]);
    toast.success("Agregado a tu cotización", { description: `${config.productName} · ${config.crystal}` });
  };

  const addDirect = (product, category) => {
    setCartItems((prev) => [
      ...prev,
      {
        productId: product.id,
        productName: product.name,
        brand: product.brand,
        price: product.price,
        productImage: product.image,
        type: "producto",
        category: category === "sol" ? "Lentes de sol" : category,
      },
    ]);
    toast.success("Agregado a tu cotización", { description: product.name });
  };

  const addRepair = (service) => {
    setCartItems((prev) => [
      ...prev,
      {
        productId: service.id,
        productName: service.name,
        type: "reparacion",
        price: service.quoteOnly ? null : service.price,
        priceFrom: service.priceFrom,
        paymentCash: true,
      },
    ]);
    toast.success("Servicio agregado", {
      description: service.quoteOnly
        ? `${service.name} — se cotiza según medida`
        : `${service.name} — ${formatPrice(service.price)}${service.priceFrom ? " (desde)" : ""} · pago en efectivo`,
    });
  };

  const removeItem = (idx) => setCartItems((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <div className="sticky top-0 z-40">
        <PromoMarquee />
        <Header cartCount={cartItems.length} onOpenCart={() => setCartOpen(true)} />
      </div>
      <main>
        <Hero onOpenCart={() => setCartOpen(true)} />
        <InfoStrip />

        <ProductMarquee
          testId="marquee-esencial"
          eyebrow="Colección Esencial"
          title={<>Todos a <em className="italic font-normal">un solo precio</em></>}
          description="La mayoría de nuestros marcos ópticos por un precio único. Incluyen estuche rígido + paño microfibra."
          items={mergedBasic}
          onSelect={openConfigurator}
          priceTag="$24.990"
        />

        <div id="esencial" className="sr-only" />

        <ProductMarquee
          testId="marquee-clip1"
          eyebrow="Con 1 Clip Magnético"
          title={<>Óptico <em className="italic font-normal">+ sol</em> en uno</>}
          description="Marcos con 1 clip solar magnético. Cámbialos en segundos. Incluyen estuche y paño."
          items={mergedClip1}
          onSelect={openConfigurator}
          priceTag="$34.990"
        />

        <div id="clip" className="sr-only" />

        <ProductMarquee
          testId="marquee-clip5"
          eyebrow="Con 5 Clips intercambiables"
          title={<>Cinco <em className="italic font-normal">estilos</em> en un marco</>}
          description="Marcos con 5 clips de distintos colores y tonos. Todo incluido."
          items={mergedClip5}
          onSelect={openConfigurator}
          priceTag="$39.990"
        />

        <ProductMarquee
          testId="marquee-deportivos"
          eyebrow="Deportivos"
          title={<>Rendimiento <em className="italic font-normal">y agarre</em></>}
          description="Marcos deportivos livianos con agarre de goma. Ideales para correr, andar en bici o trekking."
          items={mergedSport}
          onSelect={openConfigurator}
          priceTag="$39.990"
        />

        <div id="deportivos" className="sr-only" />

        <ProductMarquee
          testId="marquee-flexit"
          eyebrow="Flexit · Niños y Seguridad"
          title={<>Marcos <em className="italic font-normal">flexibles</em> e irrompibles</>}
          description="Flexit para niños y lentes de seguridad óptica. Flexibles, seguros, cómodos."
          items={mergedKids}
          onSelect={openConfigurator}
          priceTag="$39.990"
        />

        <SunglassesSection products={mergedSun} onAddDirect={addDirect} />
        <RepairsSection onAddRepair={addRepair} services={services} />
      </main>
      <Footer />

      <LensConfigurator open={configOpen} onOpenChange={setConfigOpen} product={activeProduct} onAdd={addOpticalConfigured} />

      <QuoteCart
        open={cartOpen}
        onOpenChange={setCartOpen}
        items={cartItems}
        onRemove={removeItem}
        name={name}
        setName={setName}
        phone={phone}
        setPhone={setPhone}
        note={note}
        setNote={setNote}
      />

      <button
        onClick={() => setCartOpen(true)}
        className="fixed bottom-6 right-6 md:hidden z-30 bg-[#1C1C1C] text-white px-5 py-4 flex items-center gap-2 shadow-lg border-b-2 border-[#B8933A]"
        data-testid="floating-cart-button"
      >
        <ShoppingBag className="w-4 h-4" />
        <span className="text-xs tracking-[0.2em] uppercase font-semibold">
          Cotizar ({cartItems.length})
        </span>
      </button>

      <Toaster position="bottom-center" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<AdminPanel />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
